import { useEffect, useMemo, useState } from 'react'

import {
  ActionStatus,
  SubscriptionLevel,
  type Post,
} from '@tribeplatform/gql-client/types'
import { useUpdateSubscription } from '@tribeplatform/react-sdk/hooks'
import { Alert } from '@tribeplatform/react-ui-kit/Alert'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'
import { SvgIcon, type SvgIconName } from '@tribeplatform/react-ui-kit/Icon'
import { toast, useToastContext } from '@tribeplatform/react-ui-kit/Toast'

import { EntityImage } from '../../common/components/EntityImage.js'
import { useLogin } from '../../common/hooks/useLogin.js'
import { useFirstRenderContext } from '../../common/ssr/index.js'
import { T } from '../../i18n/components/T.js'
import { useI18n } from '../../i18n/providers/I18nProvider.js'
import { convertIdToGlyph } from '../../MediaPicker/utils/utils.js'
import { FollowAction as TFollowAction } from '../../Views/types.js'
import { getTextIconSettings } from '../utils/utils.js'

interface Props {
  post: Post
  action: TFollowAction
}

/** null = not following */
type FollowLevel = SubscriptionLevel | null

const getFollowLevel = (post: Post): FollowLevel => {
  if (!post?.authMemberProps?.subscribed) {
    return null
  }
  return post.authMemberProps.subscriptionLevel ?? SubscriptionLevel.allReplies
}

const UnfollowedToast = ({
  title,
  onUndo,
}: {
  title: string
  onUndo: () => void
}) => {
  const { $t } = useI18n()
  const { onClose } = useToastContext()

  return (
    <Alert
      status="neutral"
      withClose
      onClose={onClose}
      title={$t(
        {
          defaultMessage: "You're no longer following {post}.",
          id: 'Notifications.Unfollowed.Post',
        },
        { post: title },
      )}
    >
      <Alert.Actions>
        <Button
          size="sm"
          variant="secondaryNeutral"
          onClick={() => {
            onUndo()
            onClose()
          }}
        >
          <T defaultMessage="Undo" id="Generics.Undo" />
        </Button>
      </Alert.Actions>
    </Alert>
  )
}

export const FollowAction = ({ post, action }: Props) => {
  const { defaultLabel, variant, glyphId, color, activeGlyphId, activeColor } =
    action
  const { $t } = useI18n()
  const { isFirstRender } = useFirstRenderContext()
  const { mutateAsync: updateSubscription } = useUpdateSubscription()
  const [level, setLevel] = useState<FollowLevel>(getFollowLevel(post))

  useEffect(() => {
    setLevel(getFollowLevel(post))
  }, [
    post?.authMemberProps?.subscribed,
    post?.authMemberProps?.subscriptionLevel,
  ])

  const { isLoggedIn, showLogin } = useLogin()
  const { showText, showIcon } = getTextIconSettings(variant)

  const applyLevel = async (next: FollowLevel) => {
    const previous = level
    setLevel(next)
    try {
      const result = await updateSubscription(
        next
          ? {
              action: 'subscribe',
              publisherType: 'post',
              publisherId: post.id,
              level: next,
            }
          : {
              action: 'unsubscribe',
              publisherType: 'post',
              publisherId: post.id,
            },
      )
      if (result?.status === ActionStatus.failed) {
        setLevel(previous)
        return
      }
      if (next === null && previous !== null) {
        toast({
          custom: (
            <UnfollowedToast
              title={post.title || ''}
              onUndo={() => applyLevel(previous)}
            />
          ),
        })
      }
    } catch {
      setLevel(previous)
    }
  }

  const levelLabels = useMemo<Record<SubscriptionLevel, string>>(
    () => ({
      [SubscriptionLevel.allReplies]: $t({
        defaultMessage: 'All replies',
        id: 'Post.Follow.AllReplies',
      }),
      [SubscriptionLevel.repliesToMe]: $t({
        defaultMessage: 'Only replies to me',
        id: 'Post.Follow.RepliesToMe',
      }),
    }),
    [$t],
  )
  const text = level ? levelLabels[level] : defaultLabel
  const following = level !== null

  const defaultIcon = showIcon && (
    <EntityImage
      entity={{
        image: convertIdToGlyph(glyphId),
        name: defaultLabel || '',
      }}
      size="lg"
      displayColor={color}
    />
  )

  const activator = (
    <Button
      variant="secondaryNeutral"
      className="group select-none"
      pressed={following}
      size="lg"
      fullWidth
      as="span"
      leadingIcon={
        following && showIcon ? (
          <EntityImage
            entity={{
              image: convertIdToGlyph(activeGlyphId || glyphId),
              name: text ?? '',
            }}
            size="lg"
            displayColor={activeColor || (color ?? '')}
            iconStyle="solid"
          />
        ) : (
          defaultIcon
        )
      }
      aria-label={text}
    >
      {showText && <span className="truncate">{text}</span>}
    </Button>
  )

  if (!isLoggedIn) {
    return (
      <div className="flex-1">
        <Button
          variant="secondaryNeutral"
          className="group select-none"
          size="lg"
          fullWidth
          leadingIcon={defaultIcon}
          aria-label={defaultLabel}
          onClick={e => {
            e.preventDefault()
            showLogin()
          }}
        >
          {showText && <span className="truncate">{defaultLabel}</span>}
        </Button>
      </div>
    )
  }

  // Avoid dropdown id hydration mismatch.
  if (isFirstRender) {
    return <div className="flex-1">{activator}</div>
  }

  const item = (value: FollowLevel, label: string, icon: SvgIconName) => (
    <Dropdown.Item
      leadingIcon={<SvgIcon name={icon} />}
      trailingAddon={level === value ? <SvgIcon name="check" /> : undefined}
      onClick={() => applyLevel(value)}
    >
      {label}
    </Dropdown.Item>
  )

  return (
    <div className="flex-1">
      <Dropdown placement="bottom-start" className="w-full">
        <Dropdown.ButtonMinimal className="w-full" aria-label={text}>
          {activator}
        </Dropdown.ButtonMinimal>
        <Dropdown.Items className="w-56">
          {item(
            SubscriptionLevel.allReplies,
            levelLabels[SubscriptionLevel.allReplies],
            'bell',
          )}
          {item(
            SubscriptionLevel.repliesToMe,
            levelLabels[SubscriptionLevel.repliesToMe],
            'bell',
          )}
          {following &&
            item(
              null,
              $t({ defaultMessage: 'Unfollow', id: 'Post.Follow.Unfollow' }),
              'bell',
            )}
        </Dropdown.Items>
      </Dropdown>
    </div>
  )
}
